// src/pages/Auth.tsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "@supabase/auth-helpers-react";
import { supabase } from "@/lib/supabase";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function Auth() {
  const session = useSession();
  const q = useQuery();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const redirectPath = q.get("redirect") || "/";

  // 1) Handle the `code` exchange (magic link or OAuth) when this page opens.
  useEffect(() => {
    const code = q.get("code");      // Supabase will append ?code=... to your redirect_to
    if (!code) return;

    // Avoid double-exchange if user refreshes
    let done = false;
    (async () => {
      setBusy(true);
      setMsg("Signing you in…");
      setErr(null);
      try {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) throw error;
        done = true;
        // At this point, session is set in localStorage by supabase-js
        navigate(redirectPath, { replace: true });
      } catch (e: any) {
        setErr(e?.message || "Could not complete sign-in.");
      } finally {
        if (!done) setBusy(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once per page load

  // 2) If already signed in, go where you meant to go
  useEffect(() => {
    if (session) navigate(redirectPath, { replace: true });
  }, [session, redirectPath, navigate]);

  // 3) Send magic link
  async function sendMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      // IMPORTANT: redirect_to must be an allowed URL in Supabase Auth Settings
      const redirectTo = `${window.location.origin}/auth?redirect=${encodeURIComponent(redirectPath)}`;

      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: { emailRedirectTo: redirectTo },
      });
      if (error) throw error;
      setMsg("Magic link sent. Check your email.");
    } catch (e: any) {
      setErr(e?.message || "Failed to send magic link.");
    } finally {
      setBusy(false);
    }
  }

  // 4) Google OAuth (optional)
  async function signInWithGoogle() {
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      const redirectTo = `${window.location.origin}/auth?redirect=${encodeURIComponent(redirectPath)}`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo },
      });
      if (error) throw error;
      // user gets redirected to Google, then back here with ?code=...
    } catch (e: any) {
      setErr(e?.message || "Google sign-in failed.");
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-md py-10">
      <h1 className="text-2xl font-semibold mb-6">Sign in to Siimply</h1>

      <form onSubmit={sendMagicLink} className="space-y-3">
        <input
          type="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full border rounded px-3 py-2"
        />
        <button
          type="submit"
          disabled={busy}
          className="w-full bg-primary text-white rounded px-3 py-2 disabled:opacity-60"
        >
          {busy ? "Working…" : "Send me a magic link"}
        </button>
      </form>

      <div className="my-6 text-center text-sm text-muted-foreground">or</div>

      <button
        onClick={signInWithGoogle}
        disabled={busy}
        className="w-full border rounded px-3 py-2 disabled:opacity-60"
      >
        Continue with Google
      </button>

      {msg && <p className="mt-4 text-green-700 text-sm">{msg}</p>}
      {err && <p className="mt-4 text-red-600 text-sm">{err}</p>}
    </div>
  );
}
